import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { AsyncPipe, NgClass, UpperCasePipe } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ApiHttpResponse,
  Author,
  CircleUserComponent,
  Coaching,
  IconComponent,
  ScenicCardComponent,
  SecondaryColorsRef,
  SkeletonComponent,
  ToggleIconButtonComponent,
  VideoSection,
} from '@kinomap-dev/ng-ui-lib';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MOBILE_BREAKPOINT } from 'app/modules/dashboard/constants/screen';
import { Playlist } from 'app/modules/dashboard/models/playlist.model';
import { DistancePipe } from 'app/modules/dashboard/pipes/distance.pipe';
import { DurationPipe } from 'app/modules/dashboard/pipes/duration.pipe';
import { BehaviorSubject, map, Observable, Subscription } from 'rxjs';
import { BrowseTypesEnum } from '../../models/browse-types.enum';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { User } from 'app/models';
import { selectProfileData } from 'app/store/me/me.selectors';
import { SearchMapComponent } from '../search/search-map/search-map.component';
import { EditPlaylistComponent } from '../scenic/components/editPlaylist/edit-playlist.component';
import { ToasterService } from 'app/services/toaster.service';
import { FavoriteApi } from '../../api/favorite.api';
import { Store } from '@ngrx/store';
import { PlaylistApi } from 'app/modules/dashboard/api/playlist.api';

@Component({
  selector: 'app-playlist-detail',
  templateUrl: './playlist-detail.component.html',
  styleUrls: ['./playlist-detail.component.scss'],
  standalone: true,
  imports: [
    ScenicCardComponent,
    CircleUserComponent,
    SkeletonComponent,
    DurationPipe,
    DistancePipe,
    AsyncPipe,
    IconComponent,
    TranslateModule,
    InfiniteScrollDirective,
    ToggleIconButtonComponent,
    UpperCasePipe,
    SearchMapComponent,
    EditPlaylistComponent,
    NgClass,
  ],
})
export class PlaylistDetailComponent implements OnInit, OnDestroy {
  private route: ActivatedRoute = inject(ActivatedRoute);
  private playlistService: PlaylistApi = inject(PlaylistApi);
  private favoriteService: FavoriteApi = inject(FavoriteApi);
  private breakpointObserver: BreakpointObserver = inject(BreakpointObserver);
  private router: Router = inject(Router);
  private store: Store = inject(Store);
  private toastService: ToasterService = inject(ToasterService);
  private translate: TranslateService = inject(TranslateService);

  editColor: SecondaryColorsRef = SecondaryColorsRef.secondary_900;
  isMapDisplayed: boolean = false;
  mobileBreakpointReached: boolean = false;
  playlistId: number;
  playlist: Playlist;
  subscriptions: Subscription[] = [];
  playlistVideos: (VideoSection | Coaching)[] = [];
  playlistVideosPage: number = 1;
  authorCircleData: Author | null = null;
  scrollContainerSelector: string = '.playlists-cards-container';
  loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  loaded$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  editionLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );
  user$: Observable<User> = this.store.select(selectProfileData);
  isEditing: boolean = false;

  backButtonClick(): void {
    window.history.back();
  }

  addPlaylistToFavorite(): void {
    if (!this.playlist.id) {
      return;
    }

    if (this.playlist.is_favorite) {
      this.subscriptions.push(
        this.playlistService
          .removePlaylistFromFavorite(this.playlist.id)
          .subscribe(() => (this.playlist.is_favorite = false))
      );
      return;
    }

    this.subscriptions.push(
      this.playlistService
        .addPlaylistToFavorite(this.playlist.id)
        .subscribe(() => (this.playlist.is_favorite = true))
    );
  }

  getVideosFromPlaylist(): void {
    this.subscriptions.push(
      this.playlistService
        .getVideoFromPlaylist(this.playlistId, this.playlistVideosPage)
        .pipe(
          map((res: ApiHttpResponse<(VideoSection | Coaching)[]>) => res.data)
        )
        .subscribe((videos: (VideoSection | Coaching)[]) => {
          this.playlistVideos = [...this.playlistVideos, ...videos];
          this.loading$.next(false);
        })
    );
  }

  nextPage(): void {
    if (this.playlistVideos.length + 1 < this.playlist.videos_count) {
      this.loading$.next(true);
      this.playlistVideosPage++;
      this.getVideosFromPlaylist();
    }
  }

  onLikeClick(video: VideoSection | Coaching): void {
    if (!video) {
      return;
    }

    if (video.is_favorite) {
      this.subscriptions.push(
        this.favoriteService
          .deleteFavorite$(video.id, BrowseTypesEnum.Video)
          .subscribe(() => (video.is_favorite = false))
      );
      return;
    }

    this.subscriptions.push(
      this.favoriteService
        .postFavorite$(video.id, BrowseTypesEnum.Video)
        .subscribe(() => (video.is_favorite = true))
    );
  }

  onPlaylistUpdated(playlist: any): void {
    this.editionLoading$.next(true);

    this.playlistService.editPlaylist(this.playlistId, playlist).subscribe({
      next: (playlist: Playlist) => {
        this.toastService.success(
          '',
          this.translate.instant(
            'watch.playlist_modal.toast.edit_playlist_success'
          )
        );
        this.playlist = playlist;
        this.isEditing = false;
      },
      error: () => {
        this.toastService.warning(
          '',
          this.translate.instant(
            'watch.playlist_modal.toast.edit_playlist_error'
          )
        );
      },
      complete: () => this.editionLoading$.next(false),
    });
  }

  onRemoveClick(videoId: number): void {
    this.playlistService
      .removeVideoFromPlaylist(this.playlistId, videoId)
      .subscribe();
    this.playlist = {
      ...this.playlist,
      videos_count: this.playlist.videos_count - 1,
    };
    this.playlistVideos = this.playlistVideos.filter(
      (video: VideoSection) => video.id !== videoId
    );
  }

  onScrollBottom(): void {
    this.nextPage();
  }

  redirectToBrowse(): void {
    this.router.navigate(['browse']);
  }

  redirect(
    id: number,
    to: 'author' | 'browse',
    type: 'scenic' | 'coaching' = 'scenic'
  ): Promise<boolean> {
    switch (to) {
      case 'author': {
        return this.router.navigate(['/dashboard/profile', id]);
      }
      case 'browse': {
        return this.router.navigate([
          `/browse/${type === 'scenic' ? 'watch' : 'coaching'}`,
          id,
        ]);
      }
    }
  }

  ngOnInit(): void {
    this.playlistId = +this.route.snapshot.paramMap.get('playlistId');

    this.subscriptions = [
      ...this.subscriptions,
      this.playlistService.getPlaylist(this.playlistId).subscribe({
        next: (playlist: Playlist) => {
          this.user$.subscribe((user: User) => {
            if (
              playlist.status === 'private' &&
              playlist.author.id !== user.id &&
              user.roles.includes('ROLE_ADMIN')
            ) {
              this.redirectToBrowse();
              return;
            }
          });

          this.playlist = playlist;

          this.authorCircleData = {
            id: playlist.author.id,
            avatar_url: playlist.author.avatar_url,
            country: playlist.author.country,
            username: playlist.author.username,
          };
          this.getVideosFromPlaylist();
        },
        error: (err: Error) => {
          console.error(err.message);
          this.redirectToBrowse();
        },
      }),
      this.breakpointObserver
        .observe([MOBILE_BREAKPOINT])
        .subscribe((state: BreakpointState) => {
          this.mobileBreakpointReached =
            state.matches && state.breakpoints[MOBILE_BREAKPOINT];
        }),
    ];
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription) =>
      subscription.unsubscribe()
    );
  }
}
